/**
 * One still photo on a reel page, cover-fit behind the caption.
 * The bytes stay in Storage or a data URL from the existing image upload.
 * No Fabric import: unit tests stay node-only.
 */

import type { ProjectPage } from '@canvas/shared-types';
import { reelClipFrame } from './reelClip';

/** Mark so a later photo replaces this one and leaves the caption and clip. */
export const REEL_IMAGE_KEY = 'craftaraReelImage';

export interface ReelImageSource {
  src: string;
  width: number;
  height: number;
  /** Storage path when the file was uploaded. The stage reads bytes from here. */
  storagePath?: string | null;
}

function isReelImage(obj: Record<string, unknown>): boolean {
  return obj[REEL_IMAGE_KEY] === true;
}

/** Cover-fit image object. A remote URL asks for CORS; a data URL does not. */
export function reelImageObject(image: ReelImageSource, pageWidth: number, pageHeight: number): Record<string, unknown> {
  const frame = reelClipFrame(
    { naturalWidth: image.width, naturalHeight: image.height },
    pageWidth,
    pageHeight
  );
  const remote = /^https?:\/\//i.test(image.src);
  const photo: Record<string, unknown> = {
    type: 'image',
    [REEL_IMAGE_KEY]: true,
    originX: 'center',
    originY: 'center',
    left: frame.left,
    top: frame.top,
    width: image.width,
    height: image.height,
    scaleX: frame.scale,
    scaleY: frame.scale,
    angle: 0,
    opacity: 1,
    src: image.src,
    filters: [],
    selectable: false,
    evented: false,
  };
  if (remote) photo.crossOrigin = 'anonymous';
  if (image.storagePath) photo.craftaraReelImagePath = image.storagePath;
  return photo;
}

/** Replace any reel photo. The caption stays in front. A clip stays in front of the photo. */
export function pageWithReelImage(page: ProjectPage, image: ReelImageSource): ProjectPage {
  const width = page.canvasData.width || 1080;
  const height = page.canvasData.height || 1920;
  const kept = (page.canvasData.objects || []).filter((obj) => !isReelImage(obj));
  const photo = reelImageObject(image, width, height);
  return { ...page, canvasData: { ...page.canvasData, objects: [photo, ...kept] } };
}
